import { View, StyleSheet } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import React, { useEffect, useState } from "react";
import UsersTable, { UsersTableProps } from "./UsersTable";
import {
  GetUsersForSchoolDocument,
  useAssignCourseToFacultyMutation,
  useGetUsersForSchoolQuery,
  useInviteUserToSchoolMutation,
  useRemoveCourseAssignmentFromFacultyMutation,
  useRevokeFacultyMutation,
} from "../../../graphql/generated/graphql";

import NewCustomAxioActionModal from "./NewCustomAxioActionModal";
import AssignCourseToFacultyListContainer from "./AssignCourseToFacultyListContainer";
import RemoveCourseFromFacultyListContainer from "./RemoveCourseFromFacultyList";
import InviteFacultyModalContainer from "./InviteFacultyModalContainer";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import usePagination from "../../../hooks/usePagination";
import { PlusCircle } from "../../../components/svgIcons";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import useSortingDropdown, { SORT_ITEMS } from "../../../hooks/useSortingDropdown";
import { useAppConfig } from "../../../AppConfigProvider";
import { UsersTabModalState } from "./types";

const modals = {
  revokeFaculty: {
    title: "Remove Teacher from Platform?",
    ariaLabel: "Remove Teacher Modal",
    warningText:
      "Following faculty will no longer have access to the platform. This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Remove Teacher",
  },
  revokeConfirmation: {
    title: "Success!",
    ariaLabel: "Revoke Confirmation Modal",
    warningText: "The faculty has been successfully revoked.",
    actionButtonText: "OK",
  },
};

const UsersTabContainer = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const [modalState, setModalState] = useState({
    state: UsersTabModalState.CLOSED,
    facultyId: "",
    facultyName: "",
  });

  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const { page, setPage, pageSize, setPageSize } = usePagination({});

  const { data: usersData, refetch: usersRefetch } = useGetUsersForSchoolQuery({
    variables: {
      searchQuery,
      schoolId,
      page,
      pageSize,
      sortBy,
    },
  });

  const totalItemsCount = usersData?.getUsersForSchool?.data.totalCount || 0;

  const refetchQueries = [
    {
      query: GetUsersForSchoolDocument,
      variables: {
        searchQuery,
        schoolId,
        page,
        pageSize,
        sortBy,
      },
    },
  ];

  const [assignCourseToFaculty] = useAssignCourseToFacultyMutation();
  const [removeCourseFromFaculty] = useRemoveCourseAssignmentFromFacultyMutation();
  const [inviteUserToSchool] = useInviteUserToSchoolMutation();
  const [revokeFaculty] = useRevokeFacultyMutation();

  const inviteMember = async ({
    email,
    name,
    role,
  }: {
    email: string;
    name: string;
    role: "School Admin" | "Faculty" | "Auditor";
  }) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUserToSchool({
        variables: {
          input: {
            email,
            name,
            role,
          },
        },
        refetchQueries: refetchQueries,
      });
      setIsSubmitting(false);
      return result.data?.inviteUserToSchool?.success;
    } catch (error) {
      setIsSubmitting(false);
      return false;
    }
  };

  const assignCourse = async (courseId: string) => {
    await assignCourseToFaculty({
      variables: {
        userIds: [modalState.facultyId],
        courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: UsersTabModalState.CLOSED, facultyId: "", facultyName: "" });
  };

  const removeCourse = async (courseId: string) => {
    await removeCourseFromFaculty({
      variables: {
        userIds: [modalState.facultyId],
        courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: UsersTabModalState.CLOSED, facultyId: "", facultyName: "" });
  };

  const handleRevokeFaculty = async () => {
    await revokeFaculty({
      variables: {
        userId: modalState.facultyId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: UsersTabModalState.REVOKE_CONFIRMATION, facultyId: "", facultyName: "" });
    setSearchQuery("");
  };

  const handleActionModalOpen = async (facultyId: string, facultyName: string, modalType: UsersTabModalState) => {
    setModalState({ state: modalType, facultyId, facultyName });
  };

  const handleModalClose = () => {
    setModalState({ state: UsersTabModalState.CLOSED, facultyId: "", facultyName: "" });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    usersRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <CustomText text="Users" size="l" weight="bold" style={styles.headerText} />
            <NewCustomActionButton
              text="Invite"
              onPress={() => setModalState({ state: UsersTabModalState.INVITE, facultyId: "", facultyName: "" })}
              leftIcon={<PlusCircle />}
            />
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Users" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${usersData?.getUsersForSchool.data.totalCount || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
            </View>
            <View style={styles.controlsContainer}>
              <SortingDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
                label="Sort by"
              />
              <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          </View>
        </View>
        <UsersTable
          faculties={(usersData?.getUsersForSchool?.data.faculties || []) as unknown as UsersTableProps["faculties"]}
          onActionModalOpen={handleActionModalOpen}
          resendInvite={inviteMember}
        />
        {totalItemsCount > 0 && (
          <TableControlsContainer
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalItemsCount}
          />
        )}
      </View>
      <NewCustomAxioActionModal
        visible={modalState.state === UsersTabModalState.REVOKE}
        onClose={handleModalClose}
        onAction={handleRevokeFaculty}
        modalTexts={modals.revokeFaculty}
        contextualText={modalState.facultyName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === UsersTabModalState.REVOKE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.revokeConfirmation}
        contextualText={modalState.facultyName}
      />
      <AssignCourseToFacultyListContainer
        visible={modalState.state === UsersTabModalState.ASSIGN_COURSE}
        onClose={handleModalClose}
        onAction={assignCourse}
      />
      <RemoveCourseFromFacultyListContainer
        visible={modalState.state === UsersTabModalState.REMOVE_COURSE}
        onClose={handleModalClose}
        onAction={removeCourse}
        userId={modalState.facultyId}
      />
      <InviteFacultyModalContainer
        visible={modalState.state === UsersTabModalState.INVITE}
        onClose={handleModalClose}
        onSubmit={inviteMember}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    gap: 24,
    zIndex: 3000,
    elevation: 3000,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerText: {
    color: "#000",
  },
  subHeaderText: {
    color: "#000",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
});

export default UsersTabContainer;
